<template>
  <div >
    <!-- Get serviceable address details or display message if address not serviceable -->
    <serviceable-address-search @input="setPotentialServiceableAddressData" @form-state="setAddressPotentiallyServiceable"/>

    <div class="container" v-if="addressPotentiallyServiceable">
      <!-- Select customer details, if required -->
      <transition v-on:enter="scrolldown('customer-details-rc')">
        <div id="customer-details-rc">
          <customer-details @input="setCustomerData" @form-state="setCustomerDetailsValid" @contact-added="resetBeforeAddressForm" :service="serviceDetails.connectivity"/>
        </div>
      </transition>

      <!-- Choose speed option -->
      <transition v-on:enter="scrolldown('speed-select-rc')">
        <div v-show="customerDetailsValid" id="speed-select-rc">
          <speed-select @input="setSpeedData" @form-state="setSpeedDetailsValid" :service-details="serviceDetails"/>
        </div>
      </transition>

      <!-- Select XWi details -->
      <transition v-on:enter="scrolldown('xwi-service-rc')">
        <div v-show="speedValid" id="xwi-service-rc">
          <xwi-service @input="setRouterData" @form-state="setRouterValid" :service="serviceDetails.router"/>
        </div>
      </transition>

      <!-- Select connectivity service details -->
      <transition v-on:enter="scrolldown('connectivity-service-rc')">
        <div v-show="routerValid" id="connectivity-service-rc">
          <div v-if="['utopia', 'yellowstone_fiber', 'bountiful_fiber'].includes(provider.name)">
            <utopia-residential @input="setUtopiaDetails" @form-state="setConnectivityValid" :service="serviceDetails.connectivity"/>
          </div>
          <div v-else-if="['lehi_fiber'].includes(provider.name)">
            <strata-residential @input="setStrataData" @form-state="setConnectivityValid" :service="serviceDetails.connectivity"/>
          </div>
          <div v-else-if="['southern_ute'].includes(provider.name)">
            <bonfire-residential @input="setSouthernUteData" @form-state="setConnectivityValid" :service="serviceDetails.connectivity"/>
          </div>
        </div>
      </transition>

      <!-- Select voice service details -->
      <transition v-on:enter="scrolldown('voice-service-rc')">
        <div v-show="needsVoiceOption" id="voice-service-rc">
          <br/>
          <voice-service @input="setVoiceData" :service="serviceDetails.voice"/>
        </div>
      </transition>
      <br/>
    </div>
  </div>
</template>

<script>
import helpers from '../../../lib/helpers';
import * as types from "../../../store/mutation-types";
import ServiceableAddressSearch from '../../Signup/ServiceableAddressSearch.vue';
import UtopiaResidential from "./UtopiaResidential.vue";
import StrataResidential from "./StrataResidential.vue";
import BonfireResidential from "./BonfireResidential.vue";
import VoiceService from "./VoiceService.vue";
import SpeedSelect from "../../Signup/SpeedSelect.vue";
import XWiService from './XWiService.vue';
import CustomerDetails from './CustomerDetails.vue';
import Notifications from '../../Notifications';
import {mapGetters} from "vuex";
import logger from "../../../lib/logger";

export default {
  name: "ResidentialConnection",
  components: {
    ServiceableAddressSearch,
    UtopiaResidential,
    StrataResidential,
    BonfireResidential,
    VoiceService,
    SpeedSelect,
    'xwi-service': XWiService,
    CustomerDetails,
    Notifications
  },
  emits: ['form-state', 'form-invalid', 'form-valid', 'input'],
  props : ['value', 'initial_data'],
  data (){
    return {
      scrolldown: helpers.scrolldown,
      set_address: false,
      address_potentially_serviceable: false,
      speed_details_valid: false,
      router_valid: false,
      customer_details_valid: false,
      connectivity_valid: false,
    }
  },

  computed: {
    serviceDetails () {
      return this.ssuServiceDetails;
    },
    provider () {
      if (this.serviceDetails) {
        return this.serviceDetails.connectivity.service_class_model.provider;
      }
      return {'name': ''};
    },
    addressPotentiallyServiceable () {
      logger.debug('address potentially serviceable', this.address_potentially_serviceable && this.serviceDetails !== null)
      return this.address_potentially_serviceable && this.serviceDetails !== null;
    },
    customerDetailsValid() {
      return this.customer_details_valid;
    },
    speedValid() {
      return this.customerDetailsValid && this.addressPotentiallyServiceable && this.speed_details_valid;
    },
    routerValid() {
      return this.speedValid && this.router_valid;
    },
    connectivityValid() {
      return this.routerValid && this.connectivity_valid;
    },
    needsVoiceOption () {
      logger.debug('provider', this.provider);
      return this.connectivityValid && ['utopia', 'yellowstone_fiber', 'bountiful_fiber', 'lehi_fiber'].includes(this.provider.name);
    },
    serviceValid () {
      return this.connectivityValid;
    },
    ...mapGetters([
      'ssuServiceDetails',
    ])
  },

  methods:{
    setAddressPotentiallyServiceable(address_potentially_serviceable) {
      logger.debug('set address potentially serviceable', address_potentially_serviceable);
      this.address_potentially_serviceable = address_potentially_serviceable;
    },
    setPotentialServiceableAddressData(details) {
      logger.debug('set potential serviceable address data', details);
      if (this.address_potentially_serviceable){
        this.$store.commit(types.SELF_SIGNUP_INITIALIZE_CONNECTIVITY_CLOT, details);
        if (!this.set_address) {
          this.set_address = true;
        } else {
          // the service address was changed and everything should be updated and re-rendered where possible
          this.$emit('form-invalid');
          this.$emit('form-state', false);
          this.resetAfterAddressForm();
          // thrash the variable address_potentially_serviceable to cause v-if to re-render components.
          // but we must wait a little for some reason.
          logger.debug('resetting address form')
          this.address_potentially_serviceable = false;
          helpers.sleep(100).then(() => { this.address_potentially_serviceable = true; });
        }
      }
    },
    setCustomerDetailsValid(customer_details_valid) {
      logger.debug('customer details valid', customer_details_valid);
      this.customer_details_valid = customer_details_valid;
    },
    setCustomerData(details) {
      if (this.customerDetailsValid){
        logger.debug('customer details updated', details)
        this.$store.commit(types.SELF_SIGNUP_CONNECTIVITY_SET_CUSTOMER, details);
      }
    },
    setSpeedDetailsValid(speed_details_valid) {
      this.speed_details_valid = speed_details_valid;
    },
    setSpeedData(details) {
      logger.debug('speed details updated', details)
      this.$store.commit(types.SELF_SIGNUP_CONNECTIVITY_SET_SPEED, details);
    },
    setRouterValid(router_valid) {
      logger.debug('router valid:', router_valid);
      this.router_valid = router_valid;
    },
    setRouterData (details) {
      logger.debug('router details updated', details)
      this.$store.commit(types.SELF_SIGNUP_CONNECTIVITY_SET_ROUTER, details);
    },
    setConnectivityValid(connectivity_valid) {
      logger.debug('connectivity valid:', connectivity_valid);
      this.connectivity_valid = connectivity_valid;
    },
    setUtopiaDetails(details) {
      logger.debug('utopia details updated', details)
      this.$store.commit(types.SELF_SIGNUP_CONNECTIVITY_SET_UTOPIA, details);
    },
    setStrataData(details) {
      logger.debug('strata details updated', details)
      this.$store.commit(types.SELF_SIGNUP_CONNECTIVITY_SET_STRATA, details);
    },
    setSouthernUteData(details) {
      logger.debug('southern ute details updated', details)
      this.$store.commit(types.SELF_SIGNUP_CONNECTIVITY_SET_SOUTHERN_UTE, details);
    },
    setVoiceData (details) {
      logger.debug('voice details updated', details)
      this.$store.commit(types.SELF_SIGNUP_CONNECTIVITY_SET_VOICE, details);
    },
    resetAfterAddressForm() {
      this.speed_details_valid = false;
      this.router_valid = false;
      this.customer_details_valid = false;
      this.connectivity_valid = false;
      this.$store.commit(types.SET_SERVICE_CLASS_ORDER, ['connectivity', 'xwi', 'voice', 'subnet', 'static_ip']);
    },
    resetBeforeAddressForm() {
      this.address_potentially_serviceable = false;
      this.resetAfterAddressForm();
    }
  },

  watch: {
    serviceValid (new_val) {
      this.$emit('form-state', new_val)
      this.$emit(new_val ? 'form-valid' : 'form-invalid');
    },
    initial_data () {
      this.initFromAddress();
    },
    addressPotentiallyServiceable(new_val) {
      new_val ? this.resetAfterAddressForm() : this.resetBeforeAddressForm();
    }
  }
}
</script>



// WEBPACK FOOTER //
// ResidentialConnection.vue?eb2e4422